import {
  normalizeFinancialAidAcademicYearLabel as normalizeFinancialAidAcademicYearLabelValueObject,
} from '@triedge/financial-aid-domain/value-objects';
import {
  FinancialAidCycleSchema,
  getFinancialAidCycleForYear,
  resolveCurrentFinancialAidSchoolYear,
  resolveFinancialAidCycleStatus,
  type FinancialAidCycleStatus,
  type FinancialAidSettings,
} from '@triedge/staff-settings-domain';

const readText = (value: unknown): string => String(value ?? '').trim();

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

export type FinancialAidDisplayYears = {
  academicYear: string | null;
  actualYear: number | null;
  estimateYear: number | null;
  currentCampYear: number | null;
  priorCampYear: number | null;
  hasCycleAuthority: boolean;
};

const parseFinancialAidAcademicYearRange = (academicYear: unknown): { startYear: number; endYear: number; label: string } | null => {
  const label = normalizeFinancialAidAcademicYearLabelValueObject(academicYear);
  if (!label) return null;
  const match = label.match(/^(\d{4})-(\d{4})$/);
  if (!match) return null;
  const startYear = Number(match[1]);
  const endYear = Number(match[2]);
  return {
    startYear,
    endYear,
    label,
  };
};

export const normalizeFinancialAidAcademicYearLabel = (academicYear: unknown): string | null =>
  normalizeFinancialAidAcademicYearLabelValueObject(academicYear);

export const resolveCurrentFinancialAidAcademicYear = (nowMs = Date.now()): string =>
  resolveCurrentFinancialAidSchoolYear(nowMs);

export const doFinancialAidAcademicYearsMatch = (left: unknown, right: unknown): boolean => {
  const normalizedLeft = normalizeFinancialAidAcademicYearLabel(left);
  const normalizedRight = normalizeFinancialAidAcademicYearLabel(right);
  return Boolean(normalizedLeft && normalizedRight && normalizedLeft === normalizedRight);
};

export const resolveFinancialAidDisplayYears = (academicYear: unknown): FinancialAidDisplayYears => {
  const parsed = parseFinancialAidAcademicYearRange(academicYear);
  if (!parsed) {
    return {
      academicYear: null,
      actualYear: null,
      estimateYear: null,
      currentCampYear: null,
      priorCampYear: null,
      hasCycleAuthority: false,
    };
  }
  return {
    academicYear: parsed.label,
    // School convention (YST paper form): for cycle X to X+1, parents report
    // gross income for calendar year X and estimate X+1. Confirmed by the
    // school office for the 2026-2027 season: Gross 2026 / Estimated 2027.
    actualYear: parsed.startYear,
    estimateYear: parsed.startYear + 1,
    currentCampYear: parsed.startYear,
    priorCampYear: parsed.startYear - 1,
    hasCycleAuthority: true,
  };
};

const resolveFinancialAidAcademicYearAuthorityFromSettings = (settingsState: unknown): string | null => {
  const state = asRecord(settingsState);
  const financialAid = asRecord(state.financialAid);
  const currentCycle = getFinancialAidCycleForYear(
    financialAid as FinancialAidSettings,
    resolveCurrentFinancialAidAcademicYear(),
  );
  const parsedCurrentYear = normalizeFinancialAidAcademicYearLabel(currentCycle?.schoolYear);
  if (parsedCurrentYear) return parsedCurrentYear;

  const cycle = asRecord(financialAid.cycle);
  const configuredYear = readText(cycle.schoolYear);
  const parsedConfiguredYear = normalizeFinancialAidAcademicYearLabel(configuredYear);
  if (parsedConfiguredYear) return parsedConfiguredYear;

  const academicYear = asRecord(state.academicYear);
  const academicYearLabel = readText(academicYear.label);
  const parsedAcademicYearLabel = normalizeFinancialAidAcademicYearLabel(academicYearLabel);
  if (parsedAcademicYearLabel) return parsedAcademicYearLabel;

  const startDateYear = readText(academicYear.startDate).match(/^(\d{4})-/)?.[1];
  const startYear = Number(startDateYear);
  if (Number.isFinite(startYear) && startYear > 0) {
    return `${startYear}-${startYear + 1}`;
  }

  return null;
};

export const resolveFinancialAidAcademicYearFromSettings = (settingsState: unknown): string => {
  const configuredAuthority = resolveFinancialAidAcademicYearAuthorityFromSettings(settingsState);
  if (configuredAuthority) return configuredAuthority;

  const currentYear = new Date().getFullYear();
  return `${currentYear}-${currentYear + 1}`;
};

const resolveFinancialAidSelectedCycleAcademicYearFromSettings = (settingsState: unknown): string | null => {
  const state = asRecord(settingsState);
  const financialAid = asRecord(state.financialAid);
  const cycle = asRecord(financialAid.cycle);
  return normalizeFinancialAidAcademicYearLabel(readText(cycle.schoolYear));
};

export const resolveFinancialAidOpenCycleAcademicYear = (openCycleInput: unknown): string | null => {
  const openCycle = asRecord(openCycleInput);
  const candidates = [
    openCycle.academicYear,
    openCycle.schoolYear,
  ];
  for (const candidate of candidates) {
    const parsed = normalizeFinancialAidAcademicYearLabel(candidate);
    if (parsed) return parsed;
  }
  return null;
};

export const resolveFinancialAidStaffInviteAcademicYearInputValue = (input: {
  currentAcademicYear?: unknown;
  defaultAcademicYear?: unknown;
  guardedCycleAuthority?: boolean;
  manuallyEdited?: boolean;
  noOpenCycleAuthorityReady?: boolean;
}): string => {
  if (input.guardedCycleAuthority || input.noOpenCycleAuthorityReady) {
    return "";
  }

  const currentAcademicYear = readText(input.currentAcademicYear);
  if (input.manuallyEdited && currentAcademicYear) {
    return currentAcademicYear;
  }

  const defaultAcademicYear = normalizeFinancialAidAcademicYearLabel(input.defaultAcademicYear)
    ?? readText(input.defaultAcademicYear);
  return defaultAcademicYear;
};

export const resolveFinancialAidStaffInviteCycleOpen = (input: {
  academicYear?: unknown;
  openCycle?: unknown;
  openCycleReady?: boolean;
  settingsState?: unknown;
  nowMs?: number;
}): boolean => {
  const academicYear = normalizeFinancialAidAcademicYearLabel(input.academicYear);
  if (!academicYear) return false;

  const selectedCycleStatus = resolveFinancialAidCycleStatusFromSettings(
    input.settingsState,
    academicYear,
    input.nowMs,
  );
  if (selectedCycleStatus) {
    return selectedCycleStatus.isOpen === true && selectedCycleStatus.isClosed !== true;
  }

  const openCycle = asRecord(input.openCycle);
  const openCycleAcademicYear = resolveFinancialAidOpenCycleAcademicYear(openCycle);
  return input.openCycleReady !== false
    && openCycle.isOpen === true
    && openCycle.isClosed !== true
    && doFinancialAidAcademicYearsMatch(openCycleAcademicYear, academicYear);
};

export const resolveFinancialAidStaffInviteAcademicYear = (input: {
  openCycle?: unknown;
  openCycleReady?: boolean;
  settingsState?: unknown;
  nowMs?: number;
}): string | null => {
  const openCycleAcademicYear = resolveFinancialAidOpenCycleAcademicYear(input.openCycle);
  const backendOpenCycleReady = input.openCycleReady !== false;
  const backendOpenCycleAvailable = Boolean(openCycleAcademicYear && resolveFinancialAidStaffInviteCycleOpen({
    academicYear: openCycleAcademicYear,
    openCycle: input.openCycle,
    openCycleReady: input.openCycleReady,
    settingsState: input.settingsState,
    nowMs: input.nowMs,
  }));
  if (!backendOpenCycleReady || !backendOpenCycleAvailable) {
    return null;
  }

  const selectedCycle = resolveFinancialAidSelectedCycleAcademicYearFromSettings(input.settingsState);
  if (selectedCycle && resolveFinancialAidStaffInviteCycleOpen({
    academicYear: selectedCycle,
    openCycle: input.openCycle,
    openCycleReady: input.openCycleReady,
    settingsState: input.settingsState,
    nowMs: input.nowMs,
  })) {
    return selectedCycle;
  }

  return openCycleAcademicYear;
};

export const resolveEffectiveFinancialAidAcademicYear = (input: {
  inviteAcademicYear?: unknown;
  openCycle?: unknown;
  settingsState?: unknown;
}): string => {
  const rawInviteAcademicYear = readText(input.inviteAcademicYear);
  if (rawInviteAcademicYear) {
    return normalizeFinancialAidAcademicYearLabel(rawInviteAcademicYear) ?? rawInviteAcademicYear;
  }

  const openCycle = asRecord(input.openCycle);
  const candidates = [
    openCycle.academicYear,
    openCycle.schoolYear,
  ];
  for (const candidate of candidates) {
    const parsed = normalizeFinancialAidAcademicYearLabel(candidate);
    if (parsed) return parsed;
  }
  return resolveFinancialAidAcademicYearFromSettings(input.settingsState);
};

export const resolveFinancialAidDisplayAcademicYear = (input: {
  inviteAcademicYear?: unknown;
  openCycle?: unknown;
  settingsState?: unknown;
}): string | null => {
  const openCycle = asRecord(input.openCycle);
  const candidates = [
    input.inviteAcademicYear,
    openCycle.academicYear,
    openCycle.schoolYear,
    resolveFinancialAidAcademicYearAuthorityFromSettings(input.settingsState),
  ];
  for (const candidate of candidates) {
    const parsed = parseFinancialAidAcademicYearRange(candidate);
    if (parsed) return parsed.label;
  }
  return null;
};

export const resolveFinancialAidCycleStatusFromSettings = (
  settingsState: unknown,
  academicYear: unknown,
  nowMs = Date.now(),
): FinancialAidCycleStatus | null => {
  const requestedYear = normalizeFinancialAidAcademicYearLabel(academicYear) ?? readText(academicYear);
  if (!requestedYear) return null;

  const state = asRecord(settingsState);
  const financialAid = asRecord(state.financialAid);
  const cyclesRecord = asRecord(financialAid.cycles);
  const mappedCycleEntry = Object.entries(cyclesRecord).find(([cycleYear]) => (
    doFinancialAidAcademicYearsMatch(cycleYear, requestedYear)
  ));
  const mappedCycle = asRecord(mappedCycleEntry?.[1]);
  const parsedMappedCycle = FinancialAidCycleSchema.safeParse({
    ...mappedCycle,
    schoolYear: requestedYear,
  });
  if (parsedMappedCycle.success) {
    return resolveFinancialAidCycleStatus(parsedMappedCycle.data, nowMs);
  }

  const primaryCycle = asRecord(financialAid.cycle);
  if (doFinancialAidAcademicYearsMatch(primaryCycle.schoolYear, requestedYear)) {
    const parsedPrimaryCycle = FinancialAidCycleSchema.safeParse({
      ...primaryCycle,
      schoolYear: requestedYear,
    });
    if (parsedPrimaryCycle.success) {
      return resolveFinancialAidCycleStatus(parsedPrimaryCycle.data, nowMs);
    }
  }

  const cycle = getFinancialAidCycleForYear(
    financialAid as unknown as FinancialAidSettings,
    requestedYear,
  );
  return cycle ? resolveFinancialAidCycleStatus(cycle, nowMs) : null;
};
